/**
 * Utilitários para formatação de dados no módulo de fossas sépticas
 */

/**
 * Formata um CPF/CNPJ para exibição
 * @param {string} value - CPF ou CNPJ a ser formatado
 * @returns {string} CPF/CNPJ formatado
 */
export const formatCpfCnpj = (value) => {
  if (!value) return '';
  
  // Remove caracteres não numéricos
  const numbers = value.replace(/\D/g, '');
  
  // Verifica se é CPF ou CNPJ
  if (numbers.length <= 11) {
    // CPF: 000.000.000-00
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    // CNPJ: 00.000.000/0000-00
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
};

/**
 * Formata um número de telefone para exibição
 * @param {string} value - Número de telefone a ser formatado
 * @returns {string} Telefone formatado
 */
export const formatPhone = (value) => {
  if (!value) return '';
  
  // Remove caracteres não numéricos
  const numbers = value.replace(/\D/g, '');
  
  // Verifica o tamanho para determinar se é celular ou fixo
  if (numbers.length === 11) {
    // Celular: (00) 00000-0000
    return numbers
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  } else if (numbers.length === 10) {
    // Fixo: (00) 0000-0000
    return numbers
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  
  return numbers;
};

/**
 * Formata uma data para exibição
 * @param {string} value - Data em formato ISO ou objeto Date
 * @returns {string} Data formatada (DD/MM/YYYY)
 */
export const formatDate = (value) => {
  if (!value) return '';
  
  const date = new Date(value);
  
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('pt-BR');
};

/**
 * Formata coordenadas para exibição
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {string} Coordenadas formatadas
 */
export const formatCoordinates = (lat, lng) => {
  if (lat === undefined || lng === undefined) return '';
  
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  
  const latAbs = Math.abs(lat).toFixed(6);
  const lngAbs = Math.abs(lng).toFixed(6);
  
  return `${latAbs}° ${latDir}, ${lngAbs}° ${lngDir}`;
};

/**
 * Formata o status da propriedade para exibição com cor
 * @param {string} status - Status da propriedade
 * @returns {object} Objeto com texto e cor
 */
export const formatStatus = (status) => {
  switch (status) {
    case 'Mapeada':
      return { text: 'Mapeada', color: '#28a745' }; // Verde
    case 'Não Mapeada':
      return { text: 'Não Mapeada', color: '#dc3545' }; // Vermelho
    case 'Em Análise':
      return { text: 'Em Análise', color: '#ffc107' }; // Amarelo
    default:
      return { text: status || 'Desconhecido', color: '#6c757d' }; // Cinza
  }
};

/**
 * Formata o status da visita para exibição com cor
 * @param {string} status - Status da visita
 * @returns {object} Objeto com texto e cor
 */
export const formatVisitStatus = (status) => {
  switch (status) {
    case 'Realizada':
      return { text: 'Realizada', color: '#28a745' }; // Verde
    case 'Ausente':
      return { text: 'Ausente', color: '#ffc107' }; // Amarelo
    case 'Remarcada':
      return { text: 'Remarcada', color: '#17a2b8' }; // Azul
    default:
      return { text: status || 'Desconhecido', color: '#6c757d' }; // Cinza
  }
};

/**
 * Formata o tipo de fossa séptica para exibição
 * @param {string} tipo - Tipo da fossa séptica
 * @returns {string} Tipo formatado
 */
export const formatFossaType = (tipo) => {
  switch (tipo) {
    case 'Convencional':
      return 'Fossa Séptica Convencional';
    case 'Biodigestora':
      return 'Fossa Biodigestora';
    case 'Ecologica':
      return 'Fossa Ecológica';
    case 'Sumidouro':
      return 'Sumidouro';
    default:
      return tipo || 'Não especificado';
  }
};

/**
 * Formata um número para exibição
 * @param {number} value - Número a ser formatado
 * @returns {string} Número formatado
 */
export const formatNumber = (value) => {
  if (value === undefined || value === null) return '';
  
  return new Intl.NumberFormat('pt-BR').format(value);
};